import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdPushPin, MdArchive, MdNotificationsOff, MdMoreVert, MdDelete } from 'react-icons/md';
import { getConnectionBadgeLabel, CONNECTION_BADGE_COLORS } from './connectionBadge';
import { useIsMobile } from '../../hooks/useIsMobile';
import OptionsBottomSheet from './OptionsBottomSheet';

const MENU_Z_INDEX = 9999;

function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) {
    return date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Juče';
  if (diffDays < 7) return date.toLocaleDateString('sr-RS', { weekday: 'short' });
  return date.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short' });
}

export default function ConversationListItem({
  conversation,
  otherUserDisplayName,
  otherUserPhotoUrl,
  connectionBadgeLabel,
  isSelected,
  isUnread,
  isPinned,
  isArchived,
  isMuted,
  onSelect,
  onPin,
  onArchive,
  onMute,
  onDelete,
  onMarkRead,
  onOpenMenu,
  t = (s) => s,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const isMobile = useIsMobile();

  const lastMessage = conversation.lastMessage;
  const unreadCount = conversation.unreadCount ?? 0;

  useEffect(() => {
    if (!menuOpen) {
      setDropdownRect(null);
      return;
    }
    if (!isMobile && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, right: window.innerWidth - r.right, width: 192 });
    }
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen, isMobile]);

  const sheetOptions = [
    { label: isPinned ? 'Ukloni oznaku' : 'Označi', icon: <MdPushPin className="text-lg" />, onClick: () => onPin(conversation) },
    { label: isArchived ? 'Vrati iz arhive' : 'Arhiviraj', icon: <MdArchive className="text-lg" />, onClick: () => onArchive(conversation) },
    { label: isMuted ? 'Ukloni utišavanje' : 'Utisaj', icon: <MdNotificationsOff className="text-lg" />, onClick: () => onMute(conversation) },
    { label: 'Obriši razgovor', icon: <MdDelete className="text-lg" />, onClick: () => onDelete(conversation), danger: true },
  ];

  const badgeClass = CONNECTION_BADGE_COLORS[connectionBadgeLabel] || CONNECTION_BADGE_COLORS['Ostalo'];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(conversation)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(conversation);
        }
      }}
      className={`
        group flex items-start gap-3 w-full px-4 py-3 text-left rounded-xl transition-all
        border shadow-sm
        hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-200 dark:hover:border-gray-500 hover:shadow
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset
        ${isSelected ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700 shadow-md' : 'border-gray-200 dark:border-gray-600'}
        ${isUnread ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''}
      `}
      aria-label={`Razgovor sa ${otherUserDisplayName}${unreadCount > 0 ? `, ${unreadCount} nepročitano` : ''}`}
    >
      <div className="relative flex-shrink-0">
        <div className="w-11 h-11 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm overflow-hidden">
          {otherUserPhotoUrl ? (
            <img src={otherUserPhotoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            (otherUserDisplayName || 'U')[0].toUpperCase()
          )}
        </div>
        {/* Online dot placeholder - no layout shift; can be wired to presence later */}
        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-600 border-2 border-white dark:border-gray-800" aria-hidden />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-semibold text-sm truncate ${isUnread ? 'text-gray-900 dark:text-gray-100' : 'text-gray-800 dark:text-gray-200'}`}>
            {otherUserDisplayName || 'Korisnik'}
          </span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${badgeClass}`}>
            {connectionBadgeLabel}
          </span>
          {isMuted && (
            <MdNotificationsOff className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" title="Utisano" />
          )}
        </div>
        {lastMessage && (
          <p className={`text-xs mt-0.5 truncate ${isUnread ? 'text-gray-700 dark:text-gray-300 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
            {lastMessage.content?.startsWith('data:image') ? '🖼 Slika' : lastMessage.content}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {lastMessage && (
          <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
            {formatTime(lastMessage.createdAt)}
          </span>
        )}
        {unreadCount > 0 && (
          <span className="flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary-500 text-white text-xs font-semibold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        <div className="relative" ref={menuRef}>
          <button
            ref={triggerRef}
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Više akcija"
            aria-expanded={menuOpen}
          >
            <MdMoreVert className="text-lg" />
          </button>
          {isMobile && (
            <OptionsBottomSheet
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              options={sheetOptions}
              title="Razgovor"
            />
          )}
          {!isMobile && menuOpen && dropdownRect && createPortal(
            <div
              className="fixed py-1 w-48 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl"
              style={{ top: dropdownRect.top, right: dropdownRect.right, zIndex: MENU_Z_INDEX }}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" onClick={(e) => { e.stopPropagation(); onPin(conversation); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <MdPushPin className="text-base" />{isPinned ? 'Ukloni oznaku' : 'Označi'}
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); onArchive(conversation); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <MdArchive className="text-base" />{isArchived ? 'Vrati iz arhive' : 'Arhiviraj'}
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); onMute(conversation); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <MdNotificationsOff className="text-base" />{isMuted ? 'Ukloni utišavanje' : 'Utisaj'}
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(conversation); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                <MdDelete className="text-base" />Obriši razgovor
              </button>
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
}

export { formatTime };
